import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { transferAlias, transferGroupAlias } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { User } from '../../../../../types';
import SharedDialog from '../../../../shared/shared-dialog';
import SharedAutocomplete from '../../../../shared/shared-autocomplete';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  UserRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
interface TransferUserAliasDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: string[];
  user: User;
  setSelectedRows: Dispatch<SetStateAction<string[]>>;
  changeLogRoles: string[];
}

export default function TransferUserAliasDialog({
  open,
  handleClose,
  selectedRows,
  user,
  changeLogRoles,
  setSelectedRows,
}: TransferUserAliasDialogProps) {
  const { createEvent } = useAnalytics();
  const [transferLocation, setTransferLocation] = useState('user');
  const [transferTarget, setTransferTarget] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      transferLocation === 'user'
        ? transferAlias(
            user.primaryEmail,
            transferTarget,
            selectedRows,
            changeLogMessage
          )
        : transferGroupAlias(
            user.primaryEmail,
            transferTarget,
            selectedRows,
            changeLogMessage
          ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'The task to transfer user aliases has started.',
      });

      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue transfering user aliases. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.user_management.user_details.aliases.transfer_user_aliases,
        {
          module: ModuleList.modules.userManagement.name,
          section: ModuleList.modules.userManagement.sections.aliases,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, UserRoles.TransferAliases.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Transfer Alias"
      dialogContent={
        <Box>
          <Typography sx={{ mb: 2 }}>
            {selectedRows.length > 1 ? (
              <>
                Transfer <strong>{selectedRows.length} aliases</strong>.
              </>
            ) : (
              <>
                Transfer alias <strong>{selectedRows[0]}</strong>.
              </>
            )}
          </Typography>
          <SharedAutocomplete
            autoFocus
            sources={['users', 'groups']}
            autocompleteValue={transferTarget}
            setAutoCompleteValue={setTransferTarget}
            setAutoCompleteTypeValue={setTransferLocation}
            label="Search for a user or group"
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </Box>
      }
      primaryButton={{
        title: 'Transfer Alias',
        action: handleSubmit,
        loading: isPending,
        disabled: transferTarget === '',
      }}
    />
  );
}
